import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DashboardStats, Session } from '@/types';
import { toast } from '@/components/ui/use-toast';

export const useDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchDashboardData = async (retryCount = 0) => {
      try {
        setLoading(true);
        
        // Check authentication status first
        const { data: authData } = await supabase.auth.getSession();
        if (!authData.session) {
          console.log('No active session when trying to load dashboard');
          
          // If this is the first attempt, try a session refresh
          if (retryCount === 0) {
            console.log('Attempting to refresh session...');
            const { data: refreshData } = await supabase.auth.refreshSession();
            if (refreshData.session) {
              console.log('Session refreshed successfully, retrying data fetch');
              return fetchDashboardData(retryCount + 1);
            }
          }
          
          throw new Error('Authentication required to view dashboard data');
        }
        
        // Log auth info for debugging
        const token = authData.session.access_token;
        console.log(`Using auth token: ${token.substring(0, 10)}...`);
        
        // We don't need to manually set headers - the auth session should be used automatically
        // by the Supabase client for all subsequent requests
        
        console.log('Fetching clients data...');
        const { data: clients, error: clientsError } = await supabase
          .from('clients')
          .select('*');
        
        if (clientsError) {
          console.error('Error fetching clients:', clientsError);
          throw clientsError;
        }
        
        console.log(`Got ${clients?.length || 0} clients`);
        
        console.log('Fetching sessions data...');
        const { data: sessionData, error: sessionsError } = await supabase
          .from('sessions')
          .select('*')
          .order('date', { ascending: false });
        
        if (sessionsError) {
          console.error('Error fetching sessions:', sessionsError);
          throw sessionsError;
        }
        
        console.log(`Got ${sessionData?.length || 0} sessions`);

        // Generate default empty dashboard if no data exists
        if (!clients || !sessionData || clients.length === 0) {
          console.log('No client or session data found, returning empty dashboard');
          setStats({
            totalClients: 0,
            totalSessions: 0,
            totalHours: 0,
            sessionsThisMonth: 0,
            hoursThisMonth: 0,
            recentSessions: [],
            totalSupervisionHours: 0,
            totalSupervisionSessions: 0
          });
          return;
        }

        // Map session data to match the Session interface structure
        const sessions: Session[] = sessionData.map(session => {
          const mappedSession: Session = {
            id: session.id,
            user_id: session.user_id,
            client_id: session.client_id,
            date: session.date,
            duration: session.duration,
            notes: session.notes || '',
            topics: session.topics || [],
            follow_up: session.follow_up || '',
            completed: session.completed,
            created_at: session.created_at,
            updated_at: session.updated_at
          };
          
          // Only add optional fields if they exist with explicit type casting
          if ('rate' in session) mappedSession.rate = session.rate as number;
          if ('rating' in session) mappedSession.rating = session.rating as number;
          if ('goals_set' in session) mappedSession.goals_set = session.goals_set as number;
          if ('goals_achieved' in session) mappedSession.goals_achieved = session.goals_achieved as number;
          
          return mappedSession;
        });

        const totalHours = sessions.reduce((sum, session) => sum + session.duration / 60, 0);
        
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
        const sessionsThisMonth = sessions.filter(session => 
          new Date(session.date) >= new Date(firstDayOfMonth));
        const hoursThisMonth = sessionsThisMonth.reduce(
          (sum, session) => sum + session.duration / 60, 0);
        
        const recentSessions = sessions.slice(0, 5);
        
        // Safely handle supervision data, which might not exist in the schema yet
        let supervisionSessions = [];
        let supervisionError = null;
        
        try {
          console.log('Fetching supervision data...');
          const result = await supabase
            .from('supervision_sessions')
            .select('*');
          
          supervisionSessions = result.data || [];
          supervisionError = result.error;
          
          if (supervisionError) {
            console.log('Supervision table error (might not exist yet):', supervisionError);
          } else {
            console.log(`Got ${supervisionSessions.length || 0} supervision sessions`);
          }
        } catch (e) {
          console.log('Supervision table might not exist yet:', e);
          // Silently handle this error as the table might not exist
        }

        const totalSupervisionMinutes = supervisionSessions?.reduce(
          (sum, session) => sum + (session.duration || 0), 0) || 0;
        
        const dashboardStats: DashboardStats = {
          totalClients: clients.length,
          totalSessions: sessions.length,
          totalHours,
          sessionsThisMonth: sessionsThisMonth.length,
          hoursThisMonth,
          recentSessions,
          totalSupervisionHours: totalSupervisionMinutes / 60, 
          totalSupervisionSessions: supervisionSessions?.length || 0
        };
        
        console.log('Setting dashboard stats with data:', {
          clients: clients.length,
          sessions: sessions.length,
          supervisionSessions: supervisionSessions?.length || 0
        });
        
        setStats(dashboardStats);
        setError(null);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error as Error);
        
        if (retryCount < 2) {
          console.log(`Retrying dashboard data fetch (attempt ${retryCount + 1})...`);
          setTimeout(() => fetchDashboardData(retryCount + 1), 1000);
          return;
        }
        
        toast({
          title: "Error loading dashboard",
          description: "Could not fetch dashboard data. Please try again or log out and back in.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return { stats, loading, error };
};
